<div class="flex items-center justify-between mb-4">
  <h2 class="text-xl font-semibold">User sync queues</h2>
  <button pButton type="button"
          icon="pi pi-refresh"
          label="Refresh"
          (click)="reloadQueues()"
          [disabled]="queueLoading()"></button>
</div>

<p-progressSpinner *ngIf="queueLoading()" styleClass="block mx-auto my-8"/>

<p-table *ngIf="!queueLoading()" [value]="queueStates()" responsiveLayout="scroll" class="mb-6">
  <ng-template pTemplate="header">
    <tr>
      <th>Queue</th>
      <th>Queued users</th>
      <th>Listener</th>
      <th>Action</th>
    </tr>
  </ng-template>

  <ng-template pTemplate="body" let-row>
    <tr>
      <td>{{ labelFor(row.queue) }}</td>
      <td>{{ row.count }}</td>
      <td>
        <p-tag [value]="row.listenerEnabled ? 'Enabled' : 'Paused'"
               [severity]="row.listenerEnabled ? 'success' : 'danger'">
        </p-tag>
      </td>
      <td>
        <button pButton type="button"
                [label]="row.listenerEnabled ? 'Pause listener' : 'Resume listener'"
                [icon]="row.listenerEnabled ? 'pi pi-pause' : 'pi pi-play'"
                (click)="toggleListener(row)"
                [disabled]="isToggling(row.queue)"></button>
      </td>
    </tr>
  </ng-template>
</p-table>

<div class="flex items-center justify-between mb-4 mt-6">
  <h2 class="text-xl font-semibold">Circuit breaker</h2>
  <button pButton type="button"
          icon="pi pi-refresh"
          label="Refresh"
          (click)="loadCircuitBreakerStatus()"
          [disabled]="circuitBreakerLoading()"></button>
</div>

<p-progressSpinner *ngIf="circuitBreakerLoading()" styleClass="block mx-auto my-8"/>

@if (!circuitBreakerLoading() && circuitBreakerStatus(); as status) {
  <div class="flex items-center gap-2 mb-3">
    <span class="font-semibold">Status:</span>
    @if (status.trippedByErrors || status.trippedBySlowCalls) {
      <p-tag [value]="status.trippedByErrors && status.trippedBySlowCalls ? 'TRIPPED (errors + slow)' : status.trippedByErrors ? 'TRIPPED (errors)' : 'TRIPPED (slow calls)'"
             severity="danger"></p-tag>
    } @else {
      <p-tag value="OK" severity="success"></p-tag>
    }
    <span class="text-sm text-gray-500">
      (window: {{ status.windowMinutes }}min, errors: trip {{ status.errorThreshold }}/recover {{ status.errorRecoveryThreshold }}, slow: trip {{ status.failureThreshold }}/recover {{ status.slowCallRecoveryThreshold }}, slow call: {{ status.slowCallMs }}ms)
    </span>
  </div>

  <p-table [value]="[status]" responsiveLayout="scroll" class="mb-6">
    <ng-template pTemplate="header">
      <tr>
        <th>Total calls</th>
        <th>Errors (502/503)</th>
        <th>502s</th>
        <th>503s</th>
        <th>Slow calls</th>
        <th>Successes</th>
        <th>Failures</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-row>
      <tr>
        <td>{{ row.totalCalls }}</td>
        <td>
          <span [class]="row.trippedByErrors ? 'font-bold text-red-500' : ''">{{ row.errorCalls }}</span>
        </td>
        <td>{{ row.count502 }}</td>
        <td>{{ row.count503 }}</td>
        <td>
          <span [class]="row.trippedBySlowCalls ? 'font-bold text-red-500' : ''">{{ row.slowCalls }}</span>
        </td>
        <td>{{ row.successCalls }}</td>
        <td>{{ row.failedCalls }}</td>
      </tr>
    </ng-template>
  </p-table>
}

<div class="flex flex-row gap-2 align-items-center">
  <input pInputText [ngModel]="search()" (ngModelChange)="onSearchChange($event)" placeholder="Search"/>
  <p-select [options]="filterOptions" [ngModel]="selectedFilter()" (ngModelChange)="onFilterChange($event)" optionLabel="label" optionValue="value" placeholder="Filter"/>
</div>

<div class="flex flex-row gap-2 mt-2 mb-4">
  <p-button (onClick)="copyToApply()" [disabled]="selectedUsers().length === 0" icon="pi pi-user" label="Auto-register"></p-button>
  <p-button (onClick)="runAutoCreate()" [disabled]="selectedUsers().length === 0 || runningAutoCreate()" [loading]="runningAutoCreate()" icon="pi pi-play" label="Run auto-create" severity="secondary"></p-button>
</div>

<p-table [value]="sortedUsers()" [(selection)]="selectedUsers" dataKey="id"
         [scrollable]="true" scrollHeight="65vh"
         [virtualScroll]="true" [virtualScrollItemSize]="48">
  <ng-template pTemplate="header">
    <tr>
      <th><p-tableHeaderCheckbox/></th>
      <th>Id</th>
      <th>Created at</th>
      <th>Name</th>
      <th>Email</th>
      <th>Profile Status</th>
      <th>Auto Register</th>
      <th>Sync Status</th>
      <th>Sync Failures</th>
      <th>Login Status</th>
      <th>Login Failures</th>
      <th>Last Sync Error</th>
      <th>Failure Reason</th>
      <th></th>
    </tr>
  </ng-template>
  <ng-template pTemplate="body" let-user [cTypedTableRow]="sortedUsers()">
    <tr>
      <td>
        <p-tableCheckbox [value]="user"/>
      </td>
      <td>
        <app-new-window-link [id]="user.id"/>
      </td>
      <td>{{ user.createdAt | date:'M/d/yy, h:mm a' }}</td>
      <td>{{ user.firstName }} {{ user.lastName }}</td>
      <td>{{ user.email }}</td>
      <td>
        <p-tag [severity]="nycProfileStatusToTag(user.nycProfileStatus)">{{ user.nycProfileStatus }}</p-tag>
      </td>
      <td>
        @if (user.nycProfileStatus === 'not-created') {
          <p-tag [severity]="autoCreateStatusTag(user).severity">{{ autoCreateStatusTag(user).value }}</p-tag>
        }
      </td>
      <td>
        @if (user.nycProfileStatus === 'complete') {
          <p-tag [severity]="lastSyncStatusToTag(user.lastSyncStatus)">{{ user.lastSyncStatus }}</p-tag>
        }
      </td>
      <td>
        @if (user.nycProfileStatus === 'complete' && user.syncFailedCount > 0) {
          <span class="text-red-500 font-semibold">{{ user.syncFailedCount }}</span>
        }
      </td>
      <td>
        @if (user.nycProfileStatus === 'complete') {
          <p-tag [severity]="loginStatusToTag(user.loginStatus)">{{ user.loginStatus }}</p-tag>
        }
      </td>
      <td>
        @if (user.nycProfileStatus === 'complete' && user.loginFailedCount > 0) {
          <span class="text-red-500 font-semibold">{{ user.loginFailedCount }}</span>
        }
      </td>
      <td class="text-xs max-w-48 truncate" [title]="user.lastSyncError ?? ''">{{ user.lastSyncError }}</td>
      <td>{{ user.autoCreateFailureReason }}</td>
      <td>
        @if (user.ignore) {
          <p-tag severity="danger">IGNORED</p-tag>
        }
      </td>
    </tr>
  </ng-template>
</p-table>
